import Pane from 'components/Common/Pane'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import LineCheckIcon from 'LuxKit/Icons/line/LineCheckIcon'
import ModalBase from 'LuxKit/Modal/ModalBase'
import ModalBody from 'LuxKit/Modal/ModalBody'
import ModalContent from 'LuxKit/Modal/ModalContent'
import ModalFooter from 'LuxKit/Modal/ModalFooter'
import BodyTextBlock from 'LuxKit/TextBlocks/BodyTextBlock'
import BodyText from 'LuxKit/Typography/BodyText'
import Heading from 'LuxKit/Typography/Heading'
import Group from 'components/utils/Group'
import ProductPaletteProvider from 'contexts/ProductPaletteContext'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import getLoyaltyEligibleTier from 'loyalty/selectors/getLoyaltyEligibleTier'
import getLoyaltyTierDisplayName from 'loyalty/selectors/getLoyaltyTierDisplayName'
import { rem } from 'polished'
import React, { ComponentProps, FormEventHandler, useCallback, useMemo } from 'react'
import RealisticConfetti from 'react-canvas-confetti/dist/presets/realistic'
import styled from 'styled-components'
import LoyaltyProductLogo from '../LoyaltyProductLogo'
import useModalElementContext from 'hooks/Modal/useModalElementContext'
import CheckboxInput from 'LuxKit/Checkbox/CheckboxInput'
import { SubscriptionSource } from 'api/LESubscriptions'
import { updateLESubscriptionsV2 } from 'actions/LESubscriptionsActions'
import AccountAccessLegalText from 'components/Account/AccountAccess/AccountAccessLegalText'
import LinePlusIcon from 'LuxKit/Icons/line/LinePlusIcon'
import LineChairWithAirplaneIcon from 'LuxKit/Icons/line/LineChairWithAirplaneIcon'
import LineCrownIcon from 'LuxKit/Icons/line/LineCrownIcon'
import LineStarIcon from 'LuxKit/Icons/line/LineStarIcon'
import LineTaxiIcon from 'LuxKit/Icons/line/LineTaxiIcon'
import { LabFeatureFlags } from 'constants/lab'
import PromotionLabel from 'LuxKit/Label/PromotionLabel'
import { useLabFeatureFlag } from 'hooks/FeatureFlag/useLabFeatureFlag'
import LoyaltyTierPill from '../LoyaltyTierPill'

const Hero = styled(Pane)`
  padding: ${rem(32)} ${rem(24)};

  &.tier-bronze {
    background: ${props => props.theme.palette.product.loyalty.bronze.gradientPrimaryBackground};
  }
  &.tier-silver {
    background: ${props => props.theme.palette.product.loyalty.silver.gradientPrimaryBackground};
  }
  &.tier-gold {
    background: ${props => props.theme.palette.product.loyalty.gold.gradientPrimaryBackground};
  }
  &.tier-platinum {
    background: ${props => props.theme.palette.product.loyalty.platinum.gradientPrimaryBackground};
  }
`

interface CopyContent {
  content: string;
  icon: React.ReactElement;
  benefitCode?: App.LoyaltyBenefit['code'];
}

const BENEFITS_COPY: Utils.PartialRecord<
  App.LoyaltyTier,
  Array<CopyContent>
> = {
  bronze: [
    { content: 'Gain Status Credits with every booking from day one', icon: <LineCheckIcon /> },
    { content: 'Earn and use points across all products', icon: <LineCheckIcon /> },
    { content: 'Double dip points with other loyalty programs', icon: <LineCheckIcon /> },
  ],
  silver: [
    { content: '1x guaranteed free room or package upgrade', icon: <LineCrownIcon />, benefitCode: 'hotel_upgrade' },
    { content: '2x free lounge passes at select airports', icon: <LineChairWithAirplaneIcon />, benefitCode: 'lounge_pass' },
    { content: 'Earn and use points across all products', icon: <LineStarIcon /> },
    { content: '24/7 priority support, tour credits, discounted travel protection, and more', icon: <LinePlusIcon /> },
  ],
  gold: [
    { content: '2x guaranteed free room or package upgrades', icon: <LineCrownIcon />, benefitCode: 'hotel_upgrade' },
    { content: '2x free airport chauffeur service', icon: <LineTaxiIcon />, benefitCode: 'airport_transfer' },
    { content: '2x free lounge passes at select airports', icon: <LineChairWithAirplaneIcon />, benefitCode: 'lounge_pass' },
    { content: '24/7 priority support, tour credits, discounted travel protection, and more', icon: <LinePlusIcon /> },
  ],
}

const CONFETTI_COLOURS: Record<App.LoyaltyTier, Array<string>> = {
  bronze: ['#EFE7E3', '#D4B5A4', '#91674E'],
  silver: ['#E7E8EA', '#BABCC2', '#6D717C'],
  gold: ['#EFE8D1', '#C6BC92', '#78704E'],
  platinum: [],
}

const CONFETTI_AUTO_RUN: NonNullable<ComponentProps<typeof RealisticConfetti>['autorun']> = { duration: 5, delay: 1, speed: 1 }

interface Props {
  onAfterClose: React.ComponentProps<typeof ModalBase>['onAfterClose']
  primaryActionCta: string
  secondaryActionCta?: string
}

function LoyaltyStayUpgradePromoJoinModal(props: Props) {
  const {
    onAfterClose,
    primaryActionCta,
    secondaryActionCta,
  } = props

  const dispatch = useAppDispatch()
  const userId = useAppSelector(state => state.auth.account.memberId)
  const isEmailSubscribed = useAppSelector(state => state.leSubscriptions.settings.emailSubscribed)
  const modalElementContext = useModalElementContext<boolean>()

  const primaryActionProps: React.ComponentProps<typeof ModalFooter>['primaryActionProps'] = useMemo(() => ({
    children: primaryActionCta,
    type: 'submit',
  }), [primaryActionCta])

  const secondaryActionProps: React.ComponentProps<typeof ModalFooter>['secondaryActionProps'] = useMemo(() => secondaryActionCta ? {
    children: secondaryActionCta,
  } : undefined, [secondaryActionCta])

  const handleSubmission = useCallback<FormEventHandler<HTMLFormElement>>((event) => {
    event.preventDefault()
    if (!isEmailSubscribed && userId) {
      dispatch(updateLESubscriptionsV2({
        userId,
        subscriptions: {
          smsSubscribed: true,
          emailSubscribed: true,
          appPushSubscribed: true,
          myJourneySubscribed: true,
          todaysEscapesSubscribed: true,
          curatedCollectionSubscribed: true,
          todaysEscapesCadence: 'DAILY',
        },
        source: SubscriptionSource.LOYALTY_ENROL_MODAL,
      }))
    }
    modalElementContext.resolve(true)
  }, [dispatch, isEmailSubscribed, modalElementContext, userId])

  const accountEligibleTier = useAppSelector(getLoyaltyEligibleTier)
  const givenName = useAppSelector((state: App.State) => state.auth.account.givenName)
  const tierName = getLoyaltyTierDisplayName(accountEligibleTier)

  const confettiColours = CONFETTI_COLOURS[accountEligibleTier]
  const confettiDecoration = useCallback<NonNullable<ComponentProps<typeof RealisticConfetti>['decorateOptions']>>((options) => {
    return {
      ...options,
      colors: confettiColours,
      origin: { y: 0.45, x: 0.5 },
      zIndex: 999,
    }
  }, [confettiColours])

  const loyaltyUpgradesPromo = useLabFeatureFlag(LabFeatureFlags.enableLoyaltyUpgradesPromo)
  const BENEFITS_PROMOTION: Utils.PartialRecord<
    App.LoyaltyTier,
    Array<{ benefitCode: App.LoyaltyBenefit['code'], promotion?: App.LabFeatureFlagResult }>
  > = useMemo(() => ({
    silver: [
      {
        promotion: loyaltyUpgradesPromo,
        benefitCode: 'hotel_upgrade',
      },
    ],
    gold: [
      {
        promotion: loyaltyUpgradesPromo,
        benefitCode: 'hotel_upgrade',
      },
    ],
  }), [loyaltyUpgradesPromo])

  // Users will only be eligible for bronze, silver, or gold at launch
  if (accountEligibleTier === 'platinum') return null

  return <ProductPaletteProvider palette={`tier-${accountEligibleTier}`}>
    <ModalBase dismissible onAfterClose={onAfterClose}>
      <form onSubmit={handleSubmission}>
        <ModalBody>
          <Hero className={`tier-${accountEligibleTier}`}>
            <Group direction="vertical" gap={16}>
              <Group direction="vertical" gap={12}>
                <Group direction="horizontal" gap={12} verticalAlign="center">
                  <LoyaltyProductLogo height={24} />
                  <LoyaltyTierPill kind="primary" tier={accountEligibleTier}>
                    {accountEligibleTier === 'bronze' && <>Exclusive invite</>}
                    {accountEligibleTier === 'silver' && <>New {tierName} benefit</>}
                    {accountEligibleTier === 'gold' && <>Updated {tierName} benefits</>}
                  </LoyaltyTierPill>
                </Group>
                <BodyText variant="small">The ultimate rewards program to elevate every escape</BodyText>
              </Group>
              <div>
                <VerticalSpacer gap={12}>
                  <Heading variant="heading2">
                    {accountEligibleTier === 'bronze' && <>
                      {givenName && <><i>{givenName}</i>, we’ve upgraded our benefits.</>}
                      {!givenName && <>We’ve upgraded our benefits.</>}
                    </>}
                    {accountEligibleTier === 'silver' && <>
                      {givenName && <><i>{givenName}</i>, you’ve got a free upgrade.</>}
                      {!givenName && <>You’ve got a free upgrade.</>}
                    </>}
                    {accountEligibleTier === 'gold' && <>
                      {givenName && <><i>{givenName}</i>, you’ve got 2 free upgrades.</>}
                      {!givenName && <>You’ve got 2 free upgrades.</>}
                    </>}
                  </Heading>
                  {accountEligibleTier === 'silver' && <BodyText variant="medium">Your <b>fast-tracked {tierName}</b> status now includes a free room or package upgrade.</BodyText>}
                  {accountEligibleTier === 'gold' && <BodyText variant="medium">Your <b>fast-tracked {tierName}</b> status now includes 2 free room or package upgrades. </BodyText>}
                </VerticalSpacer>
              </div>
            </Group>
          </Hero>
          <ModalContent>
            <VerticalSpacer gap={12}>
              <BodyText variant="large" weight="bold" colour="neutral-two">
                Enjoy instant benefits as a {tierName} member
              </BodyText>
              <VerticalSpacer gap={4}>
                {BENEFITS_COPY[accountEligibleTier]?.map((benefit) => {
                  const promotion = BENEFITS_PROMOTION[accountEligibleTier]?.find(b => b.benefitCode === benefit.benefitCode)?.promotion

                  return <Group direction="horizontal" gap={8} verticalAlign="start" tabletVerticalAlign="center" key={benefit.content}>
                    <BodyTextBlock
                      variant="medium"
                      startIcon={benefit.icon}
                      colour={promotion?.enabled ? 'highlight-secondary' : undefined}
                      weight={promotion?.enabled ? 'bold' : undefined}
                    >
                      {benefit.content}
                    </BodyTextBlock>
                    {promotion?.enabled && <PromotionLabel variant="premium">New</PromotionLabel>}
                  </Group>
                })}
              </VerticalSpacer>
              {!isEmailSubscribed && (
                <CheckboxInput
                  size="small"
                  required
                  data-testid="loyalty-join-accept-terms"
                  name="confirmTermsAndSubscribe"
                >
                  <AccountAccessLegalText />
                </CheckboxInput>
              )}
            </VerticalSpacer>
          </ModalContent>
        </ModalBody>
        <RealisticConfetti autorun={CONFETTI_AUTO_RUN} decorateOptions={confettiDecoration} />
        <ModalFooter
          primaryActionProps={primaryActionProps}
          secondaryActionProps={secondaryActionProps}
        />
      </form>
    </ModalBase>
  </ProductPaletteProvider>
}

export default LoyaltyStayUpgradePromoJoinModal
